import React from 'react';
import { Handle, Position } from 'reactflow';

const dayOptions = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const sessionOptions = [
  { value: 'us_open', label: 'US Open (13:30-20:00 UTC)' },
  { value: 'london', label: 'London (08:00-16:30 UTC)' },
  { value: 'asia', label: 'Asia (00:00-08:00 UTC)' },
];

const monthOptions = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
];

// Sensible default `value` per filter_type -- without this, switching filter_type
// leaves whatever `value` the previous type had (e.g. "monday"), which renders
// blank in a number input or as an invalid option in a differently-scoped select.
const DEFAULT_VALUE_FOR_FILTER = {
  day_of_week: 'monday',
  time_of_day: '09:30-10:30',
  session: 'us_open',
  first_hour: '1',
  fed_announcement_day: '1',
  day_of_month: 1,
  month_of_year: 'jan',
  trading_day_of_month: 1,
};

export default function TimeNode({ data, id }) {
  const onChange = (field, value) => {
    if (data.onChange) data.onChange(id, field, value);
  };

  const onFilterTypeChange = (newFilterType) => {
    onChange('filter_type', newFilterType);
    onChange('value', DEFAULT_VALUE_FOR_FILTER[newFilterType] ?? 1);
    if (newFilterType === 'trading_day_of_month') {
      onChange('direction', 'from_start');
    }
  };

  const filterType = data.filter_type || 'day_of_week';

  return (
    <div style={{
      padding: 12,
      borderRadius: 8,
      border: '2px solid #64748b',
      background: 'var(--card)',
      minWidth: 220,
      fontSize: 13,
      color: 'var(--text)',
    }}>
      <div style={{ fontWeight: 700, marginBottom: 8, color: '#94a3b8' }}>
        🕐 Time Filter
      </div>

      <select
        value={filterType}
        onChange={(e) => onFilterTypeChange(e.target.value)}
        style={{ width: '100%', marginBottom: 6, padding: 4, background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 4 }}
      >
        <option value="day_of_week">Day of Week</option>
        <option value="time_of_day">Time of Day (UTC)</option>
        <option value="session">Market Session</option>
        <option value="first_hour">First Hour</option>
        <option value="fed_announcement_day">Fed Announcement Day</option>
        <option value="day_of_month">Day of Month</option>
        <option value="month_of_year">Month of Year</option>
        <option value="trading_day_of_month">Trading Day of Month</option>
      </select>

      {filterType === 'day_of_week' && (
        <select
          value={data.value || 'monday'}
          onChange={(e) => onChange('value', e.target.value)}
          style={{ width: '100%', padding: 4, background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 4 }}
        >
          {dayOptions.map((d) => (
            <option key={d} value={d}>{d[0].toUpperCase() + d.slice(1)}</option>
          ))}
        </select>
      )}

      {filterType === 'time_of_day' && (
        <input
          type="text"
          value={data.value || '09:30-10:30'}
          onChange={(e) => onChange('value', e.target.value)}
          placeholder="HH:MM-HH:MM"
          style={{ width: '100%', padding: 4, background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 4, fontFamily: 'var(--font-mono)' }}
        />
      )}

      {filterType === 'session' && (
        <select
          value={data.value || 'us_open'}
          onChange={(e) => onChange('value', e.target.value)}
          style={{ width: '100%', padding: 4, background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 4 }}
        >
          {sessionOptions.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      )}

      {filterType === 'day_of_month' && (
        <input
          type="number"
          min={1}
          max={31}
          value={data.value || 1}
          onChange={(e) => onChange('value', Math.max(1, Math.min(31, parseInt(e.target.value, 10) || 1)))}
          style={{ width: '100%', padding: 4, background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 4, fontFamily: 'var(--font-mono)' }}
        />
      )}

      {filterType === 'month_of_year' && (
        <select
          value={data.value || 'jan'}
          onChange={(e) => onChange('value', e.target.value)}
          style={{ width: '100%', padding: 4, background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 4 }}
        >
          {monthOptions.map((m) => (
            <option key={m} value={m}>{m[0].toUpperCase() + m.slice(1)}</option>
          ))}
        </select>
      )}

      {filterType === 'trading_day_of_month' && (
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type="number"
            min={1}
            max={31}
            value={data.value || 1}
            onChange={(e) => onChange('value', Math.max(1, Math.min(31, parseInt(e.target.value, 10) || 1)))}
            style={{ width: 64, padding: 4, background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 4, fontFamily: 'var(--font-mono)' }}
          />
          <select
            value={data.direction || 'from_start'}
            onChange={(e) => onChange('direction', e.target.value)}
            style={{ flex: 1, padding: 4, background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 4 }}
          >
            <option value="from_start">From start of month</option>
            <option value="from_end">From end of month</option>
          </select>
        </div>
      )}

      <div style={{ marginTop: 6, fontSize: 10, color: 'var(--muted)', lineHeight: 1.4 }}>
        Outputs 1 when the filter matches, otherwise 0.
      </div>

      <Handle type="source" position={Position.Right} id="output" style={{ background: '#64748b' }} />
    </div>
  );
}
